import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EiqSelect } from 'eiq-storybook';
import moment from 'moment';
import { ActionType } from 'src/components/CapAction/constants';
import { CapActionDefaultProps, CapActionPropTypes } from 'src/components/CapAction/propTypes';
import { CapActionDeletePopup } from 'src/components/CapActionDeletePopup/CapActionDeletePopup';
import {
  CapEditActionTypeAndActionTaken,
  CapEditDatePickerField,
  CapEditTextField,
} from 'src/components/CapField';
import { CapActionResourceContainer } from 'src/components/CapActionResource/CapActionResourceContainer';
import { FindingSectionStatus, FindingStatus, FindingStatusOption } from 'src/components/CapBox/constants';
import { noop } from 'src/utils/core/event';
import { useCapActionDelete } from 'src/hooks/cap/useCapActionDelete';
import { useCapActionUpsert } from 'src/hooks/cap';
import Attachments from 'src/components/Attachments';
import CapActionResourceModel, {
  CapActionResourceObjectFactory,
} from 'src/components/CapActionResource/CapActionResourceModel.class';
import s from 'src/components/CapAction/CapAction.styles';
import { ACTION_RESOURCE_TYPES } from 'src/components/CapActionResource/constants';
import logger from 'src/utils/logger';

const CapActionComponent = ({
  action: actionProp,
  allExternalUsersId,
  canDonwloadAttachment,
  findingId,
  findingStatus,
  index,
  isActionEditible,
  isAdmin,
  isCapManager,
  isExternalUser,
  isFactoryUser,
  isFindingEditible,
  refetchCapHistories,
  removeActionFromCollection,
  setActionCache,
  setFindingStatusToPending,
}) => {
  const { i18n, t } = useTranslation();
  const action = useMemo(() => actionProp, [JSON.stringify(actionProp)]);
  const upsertCapAction = useCapActionUpsert();
  const deleteCapAction = useCapActionDelete();
  const [collapsed, setCollapsed] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [actionResources, setActionResources] = useState([]);
  const [actionResourcesShown, setActionResourcesShown] = useState([]);
  const tempAttachmentsMeta = action.attachmentsMeta;

  const actionRef = useRef(null);

  useEffect(() => {
    if (action && action.id) {
      const resources = action.resources?.map(resource => (
        new CapActionResourceModel(CapActionResourceObjectFactory(resource),
        )));

      const hasAnyResource = resources && resources.length !== 0;
      const capActionResource = hasAnyResource ? resources : [new CapActionResourceModel({
        actionId: action.id,
      })];

      setActionResources(capActionResource);
      setActionResourcesShown(capActionResource);
    }
    if (action && action.scrollToAction){
      actionRef.current.scrollIntoView({behavior: "smooth" , block:"end" , inline:"nearest"});
      action.scrollToAction = false;
    }
  }, [action]);

  const deleteAction = async ({ id }) => {
    if (id) {
      await deleteCapAction(id);
      removeActionFromCollection({ id });
    } else {
      removeActionFromCollection({ index });
    }
  };

  const upsertWithStateUpdate = async (args, actionId = null) => {
    try {
      const argsTemp = args;
      argsTemp.id = actionId || action.id;
      argsTemp.findingId = findingId;

      await setFindingStatusToPending();
      const response = await upsertCapAction(argsTemp);
      const newActionData = response.data.CapUpsertAction.action;
      refetchCapHistories();
      setActionCache(newActionData);

    } catch (e) {
      logger.error('Caught', e);
    }
  };

  const uploadAttachmentFile = async (actionId, meta, fileCountPendingToProcess) => {
    const newMetas = [
      ...tempAttachmentsMeta.map(({ __typename, ...rest }) => rest),
      meta,
    ];

    tempAttachmentsMeta.push(meta);
    if(fileCountPendingToProcess === 0){
      await upsertWithStateUpdate({
        attachmentIds: newMetas.map(a => a.attId),
        attachmentsMeta: newMetas,
      }, actionId);
    }
    
  };

  const onAttachmentChange = useMemo(() => {
    return async (actionId, _filename, _entity, id, meta, fileCountPendingToProcess) => {
      await uploadAttachmentFile(actionId, meta, fileCountPendingToProcess);
    };
  }, [tempAttachmentsMeta]);
  

  const attachmentComponent = () => {
    if (!isActionEditible && action.attachmentsMeta.length === 0) {
      return <s.Text/>;
    }

    return (
      <Attachments
        attachmentIds={action.attachmentsMeta}
        attachmentsLength={action.attachmentsMeta.length}
        canNotDownload={!canDonwloadAttachment}
        disabled={!isActionEditible}
        entity="attachments"
        onChange={(...args) => {
          onAttachmentChange(action.id, ...args);
        }}
        onDeleteFile={action.status === FindingStatus.Pending ? async (name, attId) => {
          if (name !== 'loading.file' && attId) {
            await upsertWithStateUpdate({
              attachmentIds: action.attachmentIds.filter(att => att !== attId),
              attachmentsMeta: action.attachmentsMeta
                .filter(att => att.attId !== attId)
                .map(att => ({
                  attId: att.attId,
                  filePath: att.filePath,
                  name: att.name,
                  size: att.size,
                  type: att.type,
                })),
            });
          }
        } : null}
        style="cap"
      />
    );
  };

  const isDeletable = (isFactoryUser && !action.actionTaken &&
    action.actionType === ActionType.Corrective &&
    !action.actualCompletionOfActionDate &&
    !action.attachmentIds.length &&
    !action.attachmentsMeta.length &&
    !action.dueDate &&
    !action.reviewNotes &&
    !action.rootCause &&
    action.status === FindingStatus.Pending &&
    !action.statusNotes) ||
    (isCapManager && action.status === FindingStatus.Pending && findingStatus !== FindingSectionStatus.Closed) ||
    (isAdmin && action.status === FindingStatus.Pending && findingStatus !== FindingSectionStatus.Closed)

  /**
   * if the action is not editable than don't show required hint on due date
   */
  const dueDateRequired = isActionEditible;

  const setResourceCache = (resourceIndex, newResource) => {
    const newCurrentResources = [...actionResources];
    const newCapActionResourceIndex = newCurrentResources.findIndex(a => a.id === newResource.id);
    if (newCapActionResourceIndex !== -1) {
      newCurrentResources[newCapActionResourceIndex] = newResource;
    } else {
      newCurrentResources.push(newResource);
    }
    setActionResources(newCurrentResources);

    const newCurrentShowingResources = [...actionResourcesShown];
    newCurrentShowingResources[resourceIndex] = newResource;
    setActionResourcesShown(newCurrentShowingResources);
  };

  const addNewResource = () => {
    const virtualResourceIndex = actionResourcesShown?.findIndex(resource => resource.isVirtualType());
    const temp = [...actionResourcesShown];
    if (virtualResourceIndex !== -1) {
      temp.splice(virtualResourceIndex, 1);
    }
    const newActionResource = new CapActionResourceModel({
      actionId: action.id,
      resourceType: ACTION_RESOURCE_TYPES.INVESTMENT,
      scrollToResouce: true,
    });
    setActionResourcesShown([...temp, newActionResource]);
  };

  const removeResourceFromList = (id) => {
    if (id) {
      const postFilteringOfRemovingId = actionResourcesShown.filter(resource => resource.id !== id);
      if (postFilteringOfRemovingId.length === 0) {
        const newActionResource = new CapActionResourceModel({
          actionId: action.id,
          resourceType: ACTION_RESOURCE_TYPES.NO_RESOURCE,
        });

        postFilteringOfRemovingId.push(newActionResource);
      }
      setActionResourcesShown(postFilteringOfRemovingId);
    }
  };

  const capActionResourceAddingInfo = () => {
    if(action && action.id) {
      const hasNoResourceFromDB = actionResourcesShown.every(resource => resource.isNew());
      if (hasNoResourceFromDB && isActionEditible && !collapsed) {
        return (
          <s.Button clickable onClick={addNewResource}>
            {t('ADD_RESOURCE')}
          </s.Button>
        );
      }

      if (!hasNoResourceFromDB) {
        const resourcesFromDBLength = actionResourcesShown.filter(resource => !resource.isNew()).length;
        return (
          <Fragment>
            <s.Button>{`${t('RESOURCES')} ${resourcesFromDBLength}`}</s.Button>
            {isActionEditible && !collapsed && (
              <s.Button add clickable onClick={addNewResource}>
                {t('ADD')}
              </s.Button>
            )}
          </Fragment>
        );
      }
    }
    return null;
  };

  

  const actionResourceComponents = () => actionResourcesShown.map((resource, resourceIndex) => {

    return <CapActionResourceContainer
      key={resource.id}
      action={action}
      actionIndex={index}
      allExternalUsersId={allExternalUsersId}
      findingId={findingId}
      findingStatus={findingStatus}
      index={resourceIndex}
      isActionEditable={isActionEditible}
      isFindingEditable={isFindingEditible}
      refetchCapHistories={refetchCapHistories}
      removeResourceFromList={removeResourceFromList}
      resource={resource}
      setResourceCache={newResource => {
        setResourceCache(resourceIndex, newResource);
      }}
    />;
  });

  if (collapsed) {
    return (
      <s.ActionDiv>
        {showDeletePopup && (
          <CapActionDeletePopup
            onClose={() => setShowDeletePopup(false)}
            onSubmit={() => deleteAction({ id: action.id })}
          />
        )}
        <s.LeftColumn>
          <s.ActionHintTextDiv>
            {index === 0 && <s.ActionHintText lang={i18n.language}>{t('ACTIONS')}</s.ActionHintText>}
          </s.ActionHintTextDiv>
          <s.LeftColumnRows>
            <s.DivRow>
              <s.CollapsedRootCauseDiv>
                <CapEditTextField
                  collapsed={true}
                  editable={isActionEditible}
                  forTitle={true}
                  onSave={v => upsertWithStateUpdate({ rootCause: v })}
                  type="text"
                  value={action.rootCause}
                />
              </s.CollapsedRootCauseDiv>
            </s.DivRow>
            <s.DivRow>{capActionResourceAddingInfo()}</s.DivRow>
          </s.LeftColumnRows>
        </s.LeftColumn>
        <s.CenterColumn>
          <s.DivRow>
            <CapEditActionTypeAndActionTaken
              actionType={action.actionType}
              editable={isActionEditible}
              expanded={false}
              onSave={({ actionType, actionTaken }) =>
                upsertWithStateUpdate({ actionTaken, actionType })
              }
              type="textarea"
              value={action.actionTaken}
            />
          </s.DivRow>
        </s.CenterColumn>
        <s.RightColumn>
          <s.ExpandSwitch
            expanded={false}
            onClick={() => setCollapsed(false)}
          />
          {isDeletable && (
            <s.RemoveButton
              collapsed={true}
              name="remove"
              onClick={() => setShowDeletePopup(true)}
            />
          )}
          <s.DivRow>
            <s.Label>{t('STATUS')}</s.Label>
            <EiqSelect 
              hasCustomChips={true} 
              isDisabled={!(!isExternalUser && isFindingEditible)}
              onChange={(_name, option) => {
                upsertWithStateUpdate({ status: option.key });
              }}
              options={FindingStatusOption} 
              value={FindingStatusOption.find(status => status.key === action.status)} 
            />
          </s.DivRow>
        </s.RightColumn>
      </s.ActionDiv>
    );
  }

  return (
    <Fragment>
      <s.ActionDiv className={Number.isNaN(Number.parseInt(action?.id, 10))? 'ignore-in-pdf':''}>
        {showDeletePopup && (
          <CapActionDeletePopup
            onClose={() => setShowDeletePopup(false)}
            onSubmit={() => deleteAction({ id: action.id })}
          />
        )}
        <s.LeftColumn ref={actionRef}>
          <s.ActionHintTextDiv>
            {index === 0 && <s.ActionHintText lang={i18n.language}>{t('ACTIONS')}</s.ActionHintText>}
          </s.ActionHintTextDiv>
          <s.LeftColumnRows>
            <s.DivRow>
              <CapEditTextField
                collapsed={false}
                editable={false}
                forTitle={false}
                onSave={noop}
                type="text"
                value={`${t('ACTION_REFERENCE_LABEL')}: ${action.actionReference || ''}`}
              />
            </s.DivRow>
            <s.DivRow>
              <CapEditTextField
                collapsed={false}
                editable={isActionEditible}
                forTitle={true}
                onSave={v => upsertWithStateUpdate({ rootCause: v })}
                type="text"
                value={action.rootCause}
              />
            </s.DivRow>
            <s.DivRow>
              <s.Label>{t('DUE_DATE')}</s.Label>
              <CapEditDatePickerField
                editable={isActionEditible}
                isRequired={dueDateRequired}
                onSave={v => upsertWithStateUpdate({ dueDate: v })}
                value={action.dueDate}
              />
            </s.DivRow>
            <s.DivRow>
              <s.Label>{t('CAP_DATE_OF_COMPLETION_OF_ACTION')}</s.Label>
              <CapEditDatePickerField
                editable={isActionEditible}
                onSave={v =>
                  upsertWithStateUpdate({ actualCompletionOfActionDate: v })
                }
                value={action.actualCompletionOfActionDate}
              />
            </s.DivRow>
            <s.DivRow>{capActionResourceAddingInfo()}</s.DivRow>
          </s.LeftColumnRows>
        </s.LeftColumn>
        <s.CenterColumn>
          <s.DivRow>
            <CapEditActionTypeAndActionTaken
              actionType={action.actionType}
              editable={isActionEditible}
              expanded={true}
              onSave={({ actionType, actionTaken }) =>
                upsertWithStateUpdate({ actionTaken, actionType })
              }
              type="textarea"
              value={action.actionTaken}
            />
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('ATTACHMENTS')}</s.Label>
            {attachmentComponent()}
          </s.DivRow>
          <s.DivRow>
            <s.ReviewNotesTitle>{t('REVIEW_NOTES')}</s.ReviewNotesTitle>
            <CapEditTextField
              action={action}
              editable={!isExternalUser && isActionEditible}
              onSave={v => upsertWithStateUpdate({ reviewNotes: v })}
              reviewNotes={true}
              type="textarea"
              value={action.reviewNotes}
            />
          </s.DivRow>
        </s.CenterColumn>
        <s.RightColumn>
          <s.ExpandSwitch
            expanded={true}
            onClick={() => setCollapsed(!collapsed)}
          />
          {isDeletable && (
            <s.RemoveButton
              name="remove"
              onClick={() => setShowDeletePopup(true)}
            />
          )}
          <s.DivRow>
            <s.Label>{t('STATUS')}</s.Label>
            <EiqSelect 
              hasCustomChips={true} 
              isDisabled={!(!isExternalUser && isFindingEditible)}
              onChange={(_name, option) => {
                upsertWithStateUpdate({ status: option.key });
              }}
              options={FindingStatusOption} 
              value={FindingStatusOption.find(status => status.key === action.status)} 
            />
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('CAP_STATUS_NOTES')}</s.Label>
            <CapEditTextField
              editable={!isExternalUser && isFindingEditible}
              isTooltipDisabled={true}
              onSave={v => upsertWithStateUpdate({ statusNotes: v })}
              type="textarea"
              value={action.statusNotes}
            />
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('CAP_STATUS_DATE')}</s.Label>
            <s.Text>
              {action.statusDate &&
              moment(new Date(action.statusDate)).format('MMMM DD, YYYY')}
            </s.Text>
          </s.DivRow>
          <s.DivRow>
            <s.Label>{t('CAP_STATUS_BY')}</s.Label>
            <s.Text>
              {action.statusUpdatedByUser &&
              `${action.statusUpdatedByUser.lastName} ${action.statusUpdatedByUser.firstName}`}
            </s.Text>
          </s.DivRow>
        </s.RightColumn>
      </s.ActionDiv>
      {actionResourcesShown.length > 0 && (
        <s.ActionResourceDiv>{actionResourceComponents()}</s.ActionResourceDiv>
      )}
    </Fragment>
  );
};

CapActionComponent.propTypes = CapActionPropTypes;
CapActionComponent.defaultProps = CapActionDefaultProps;

export const CapAction = React.memo(CapActionComponent);
